import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Fab,
  Badge,
  Typography,
  IconButton,
  TextField,
  Chip,
  Button,
  CircularProgress,
  Slide,
  Rating,
} from '@mui/material';
import {
  Chat as ChatIcon,
  KeyboardArrowDown,
  Send,
  History,
  ArrowBack,
  Star,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import chatService, {
  ChatConversation,
  ChatMessage,
} from '../../services/chatService';
import { useWebSocket } from '../../hooks/useWebSocket';
import { colors, designTokens } from '../../theme/theme';

// Chat WS URL. Vite's dev proxy does NOT forward ws:// upgrades, so dev
// hits localhost:8000 directly; prod resolves VITE_API_URL into ws[s]://.
function buildChatWsUrl(userId: string, token: string | null): string | null {
  if (!token) return null;
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  let baseHttp: string;
  if (apiUrl) {
    baseHttp = apiUrl.replace(/\/$/, '');
  } else if (typeof window !== 'undefined') {
    // Local dev: hit FastAPI directly on :8000 since vite proxy doesn't bridge ws.
    baseHttp = `${window.location.protocol}//${window.location.hostname}:8000`;
  } else {
    return null;
  }
  const wsBase = baseHttp.replace(/^http/, 'ws');
  return `${wsBase}/chat/ws/${userId}?token=${encodeURIComponent(token)}`;
}

const USER_CATEGORIES = ['Event Issue', 'Refund', 'Account', 'General'];
const PARTNER_CATEGORIES = ['Settlement', 'QR Scanner', 'Event Management', 'Account'];

export default function ChatWidget() {
  const { isAuthenticated, user } = useAuth();

  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const categories = user?.role === 'partner' ? PARTNER_CATEGORIES : USER_CATEGORIES;
  const [category, setCategory] = useState<string>(categories[0]);
  const [initialMessage, setInitialMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasPreviousConversation, setHasPreviousConversation] = useState(false);
  const [previousConvId, setPreviousConvId] = useState<string | null>(null);
  const [previousConvSubject, setPreviousConvSubject] = useState<string | null>(null);

  // C10: Previous conversation viewer
  const [viewingPrevious, setViewingPrevious] = useState(false);
  const [previousMessages, setPreviousMessages] = useState<ChatMessage[]>([]);
  const [previousLoading, setPreviousLoading] = useState(false);

  // C9: CSAT rating
  const [showRating, setShowRating] = useState(false);
  const [csatRating, setCsatRating] = useState<number | null>(null);
  const [csatComment, setCsatComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [closedConvId, setClosedConvId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // WS connects only while authenticated + widget open (no socket while
  // collapsed); the hook handles reconnect with backoff.
  const wsUrl = useMemo(() => {
    if (!isAuthenticated || !user?.id) return null;
    const token = typeof window !== 'undefined' ? localStorage.getItem('cm_token') : null;
    return buildChatWsUrl(user.id, token);
  }, [isAuthenticated, user?.id]);

  const handleWsMessage = useCallback((data: any) => {
    if (!data || typeof data !== 'object') return;
    if (data.type === 'message') {
      // Server-broadcast "message" envelope mirrors REST payload shape.
      // Only append messages for the active conversation.
      setConversation(prev => {
        if (prev && data.conversation_id === prev.id) {
          setMessages(curr => {
            // Dedupe: server may echo our own send back via broadcast.
            if (curr.some(m => m.id === data.id)) return curr;
            return [...curr, data as ChatMessage];
          });
        }
        return prev;
      });
    } else if (data.type === 'conversation_closed') {
      // Broadcast by backend when an admin resolves a conversation (chat.py).
      setConversation(prev => {
        if (!prev || data.conversation_id !== prev.id) return prev;
        setClosedConvId(prev.id);
        setShowRating(true);
        setHasPreviousConversation(true);
        setPreviousConvId(prev.id);
        setPreviousConvSubject(prev.subject || null);
        return null;
      });
    }
  }, []);

  const { isConnected: wsConnected, send: wsSend } = useWebSocket({
    url: wsUrl || '',
    enabled: !!wsUrl && open,
    onMessage: handleWsMessage,
  });

  // Once we have an active conversation + a live socket, "join" so the
  // backend routes broadcasts to us.
  useEffect(() => {
    if (wsConnected && conversation?.id) {
      wsSend({ type: 'join', conversation_id: conversation.id });
    }
  }, [wsConnected, conversation?.id, wsSend]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load active conversation on mount
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadConversation = async () => {
      setLoading(true);
      try {
        const result: any = await chatService.getActiveConversation();
        if (result && result.active) {
          const conv = result.active;
          setConversation(conv);
          setUnreadCount(conv.unread_count || 0);
          if (result.messages) {
            setMessages(result.messages);
          } else {
            const msgs = await chatService.getMessages(conv.id);
            setMessages(msgs);
          }
        } else {
          setConversation(null);
          // Check for previous archived conversation
          if (result && result.last_archived) {
            setHasPreviousConversation(true);
            setPreviousConvId(result.last_archived.id);
            setPreviousConvSubject(result.last_archived.subject);
          } else {
            setHasPreviousConversation(false);
          }
        }
      } catch {
        // No active conversation — show start form
      } finally {
        setLoading(false);
      }
    };

    loadConversation();
  }, [isAuthenticated]);

  // 60s liveness heartbeat (diagnostic only; backend answers "ping" with
  // "pong", no UI side-effect). Keeps idle proxies/LBs from tearing the
  // socket down so the next message round-trip starts on a warm
  // connection. If the socket has dropped, useWebSocket's onclose handler
  // schedules a reconnect; the heartbeat itself is a no-op when not OPEN.
  useEffect(() => {
    if (!open || !wsConnected) return;
    const id = setInterval(() => {
      wsSend({ type: 'ping' });
    }, 60_000);
    return () => clearInterval(id);
  }, [open, wsConnected, wsSend]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleStartConversation = async () => {
    if (!initialMessage.trim()) return;

    setSending(true);
    try {
      const conv = await chatService.startConversation({
        subject: `${category} Support`,
        category,
        message: initialMessage.trim(),
      });
      setConversation(conv);
      const msgs = await chatService.getMessages(conv.id);
      setMessages(msgs);
      setInitialMessage('');
      setShowRating(false);
      setRatingSubmitted(false);
    } catch {
      // Handle error silently
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversation) return;

    const content = newMessage.trim();
    setNewMessage('');
    setSending(true);

    try {
      const msg = await chatService.sendMessage(conversation.id, content);
      setMessages((prev) => [...prev, msg]);
    } catch {
      setNewMessage(content); // Restore on failure
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (conversation) {
        handleSendMessage();
      } else {
        handleStartConversation();
      }
    }
  };

  // C10: View previous conversation
  const handleViewPrevious = async () => {
    if (!previousConvId) return;
    setPreviousLoading(true);
    setViewingPrevious(true);
    try {
      const msgs = await chatService.getMessages(previousConvId);
      setPreviousMessages(msgs);
    } catch {
      setPreviousMessages([]);
    } finally {
      setPreviousLoading(false);
    }
  };

  const handleBackFromPrevious = () => {
    setViewingPrevious(false);
    setPreviousMessages([]);
  };

  // C9: Submit rating
  const handleSubmitRating = async () => {
    if (!csatRating || !closedConvId) return;
    setRatingSubmitting(true);
    try {
      await chatService.rateConversation(closedConvId, csatRating, csatComment || undefined);
      setRatingSubmitted(true);
      setShowRating(false);
    } catch {
      // silently fail
    } finally {
      setRatingSubmitting(false);
    }
  };

  const handleSkipRating = () => {
    setShowRating(false);
  };

  if (!isAuthenticated) return null;

  // Helper to render messages (shared between active and previous views)
  const renderMessages = (msgList: ChatMessage[], isReadOnly = false) => (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 2,
        py: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'rgba(255,255,255,0.1)',
          borderRadius: 2,
        },
      }}
    >
      {msgList.map((msg) => {
        const isUser = msg.sender_role === 'user' || msg.sender_role === 'partner';
        const isSystem = msg.sender_role === 'system' || (msg as any).message_type === 'system';

        if (isSystem) {
          return (
            <Box key={msg.id} sx={{ alignSelf: 'center', maxWidth: '90%', my: 0.5 }}>
              <Typography variant="caption" sx={{ color: colors.neutral[500], fontStyle: 'italic', textAlign: 'center', display: 'block', fontSize: '0.75rem' }}>
                {msg.content}
              </Typography>
            </Box>
          );
        }

        return (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isUser ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              alignSelf: isUser ? 'flex-end' : 'flex-start',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.neutral[400],
                fontSize: '0.65rem',
                mb: 0.3,
                px: 0.5,
              }}
            >
              {msg.sender_name}
            </Typography>
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: isUser
                  ? '14px 14px 4px 14px'
                  : '14px 14px 14px 4px',
                background: isUser
                  ? colors.primary.gradient
                  : designTokens.glass.surface,
                border: isUser
                  ? 'none'
                  : `1px solid ${designTokens.glass.border}`,
                opacity: isReadOnly ? 0.85 : 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: isUser ? '#FFFFFF' : colors.neutral[800],
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                }}
              >
                {msg.content}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.neutral[400],
                fontSize: '0.6rem',
                mt: 0.3,
                px: 0.5,
              }}
            >
              {new Date(msg.created_at).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          </Box>
        );
      })}
      <div ref={messagesEndRef} />
    </Box>
  );

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <Fab
          aria-label={unreadCount > 0 ? `Open support chat (${unreadCount} unread)` : 'Open support chat'}
          onClick={() => {
            setOpen(true);
            setUnreadCount(0);
          }}
          sx={{
            // Clears the 64px mobile bottom-nav on xs. z-index ladder per
            // DESIGN_SYSTEM.md §Floating Widgets: widgets 1200, snackbar 1300.
            position: 'fixed',
            bottom: { xs: 80, md: 24 },
            right: { xs: 16, md: 24 },
            width: 56,
            height: 56,
            background: colors.primary.gradient,
            color: '#FFFFFF',
            boxShadow: designTokens.shadow.glowStrong,
            zIndex: 1200,
            transition: `all ${designTokens.transition.normal}`,
            '&:hover': {
              background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
              boxShadow: designTokens.shadow.glow,
              transform: 'scale(1.08)',
            },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                bgcolor: colors.error.main,
                color: '#FFFFFF',
                fontSize: '0.65rem',
                fontWeight: 700,
                minWidth: 18,
                height: 18,
              },
            }}
          >
            <ChatIcon />
          </Badge>
        </Fab>
      )}

      {/* Chat Panel */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Box
          sx={{
            // Anchors to the Fab's corner; mobile clears bottom-nav.
            // zIndex 1200 (snackbar 1300 must stay above open chat).
            position: 'fixed',
            bottom: { xs: 80, md: 24 },
            right: { xs: 16, md: 24 },
            width: 380,
            height: 500,
            maxHeight: 'calc(100vh - 48px)',
            borderRadius: '16px',
            bgcolor: colors.background.paper,
            border: `1px solid ${designTokens.glass.border}`,
            boxShadow: designTokens.shadow.xl,
            backdropFilter: designTokens.glass.blur,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1200,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: colors.primary.gradient,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {viewingPrevious && (
                <IconButton
                  aria-label="Back to active conversation"
                  onClick={handleBackFromPrevious}
                  size="small"
                  sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#FFFFFF' } }}
                >
                  <ArrowBack sx={{ fontSize: 18 }} />
                </IconButton>
              )}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#FFFFFF',
                  fontSize: '1rem',
                }}
              >
                {viewingPrevious ? 'Previous Conversation' : 'Support'}
              </Typography>
              {/* Connection status dot — only shown for an active conversation. */}
              {!viewingPrevious && conversation && (
                <Box
                  role="status"
                  aria-label={
                    wsConnected
                      ? 'Live connection active'
                      : 'Live connection unavailable — using fallback polling'
                  }
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: wsConnected ? colors.success.main : colors.warning.main,
                    boxShadow: wsConnected
                      ? `0 0 6px ${colors.success.main}`
                      : 'none',
                    transition: `background-color ${designTokens.transition.fast}`,
                  }}
                />
              )}
            </Box>
            <IconButton
              aria-label="Close support chat"
              onClick={() => { setOpen(false); setViewingPrevious(false); }}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.15)' },
              }}
            >
              <KeyboardArrowDown />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {loading ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={32} sx={{ color: colors.primary.main }} />
              </Box>
            ) : viewingPrevious ? (
              /* C10: Previous conversation read-only view */
              <>
                <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${designTokens.glass.border}`, bgcolor: designTokens.glass.surface }}>
                  <Typography variant="caption" sx={{ color: colors.neutral[500], fontSize: '0.75rem' }}>
                    {previousConvSubject || 'Archived conversation'} (read-only)
                  </Typography>
                </Box>
                {previousLoading ? (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={24} sx={{ color: colors.primary.main }} />
                  </Box>
                ) : previousMessages.length === 0 ? (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No messages found</Typography>
                  </Box>
                ) : (
                  renderMessages(previousMessages, true)
                )}
              </>
            ) : showRating ? (
              /* C9: CSAT Rating View */
              <Box
                sx={{
                  flex: 1,
                  px: 2.5,
                  py: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Star sx={{ fontSize: 48, color: colors.accent.main }} />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: '1.1rem', textAlign: 'center' }}
                >
                  How was your experience?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.neutral[500], fontSize: '0.8rem', textAlign: 'center' }}
                >
                  Your feedback helps us improve our support.
                </Typography>

                <Rating
                  value={csatRating}
                  onChange={(_, value) => setCsatRating(value)}
                  size="large"
                  sx={{
                    '& .MuiRating-iconFilled': { color: colors.accent.main },
                    '& .MuiRating-iconHover': { color: colors.accent.light },
                    '& .MuiRating-iconEmpty': { color: colors.neutral[300] },
                  }}
                />

                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Any additional comments? (optional)"
                  value={csatComment}
                  onChange={(e) => setCsatComment(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: designTokens.radius.sm,
                      bgcolor: designTokens.glass.surface,
                      fontSize: '0.85rem',
                      '& fieldset': { borderColor: designTokens.glass.border },
                    },
                  }}
                />

                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleSkipRating}
                    sx={{
                      color: colors.neutral[500],
                      borderColor: designTokens.glass.border,
                      textTransform: 'none',
                      '&:hover': { borderColor: designTokens.glass.borderHover },
                    }}
                  >
                    Skip
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmitRating}
                    disabled={!csatRating || ratingSubmitting}
                    sx={{
                      background: colors.primary.gradient,
                      color: '#FFFFFF',
                      fontWeight: 700,
                      textTransform: 'none',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
                      },
                      '&.Mui-disabled': {
                        background: 'rgba(255,255,255,0.06)',
                        color: colors.neutral[400],
                      },
                    }}
                  >
                    {ratingSubmitting ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Submit'}
                  </Button>
                </Box>
              </Box>
            ) : conversation ? (
              /* Active conversation view */
              <>
                {/* Messages list */}
                {renderMessages(messages)}

                {/* Message input area */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderTop: `1px solid ${designTokens.glass.border}`,
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-end',
                    flexShrink: 0,
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: designTokens.radius.sm,
                        bgcolor: designTokens.glass.surface,
                        fontSize: '0.85rem',
                        '& fieldset': { borderColor: designTokens.glass.border },
                        '&:hover fieldset': { borderColor: designTokens.glass.borderHover },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.primary.main,
                          borderWidth: 1,
                        },
                      },
                    }}
                  />
                  <IconButton
                    aria-label="Send message"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sending}
                    sx={{
                      background: colors.primary.gradient,
                      color: '#FFFFFF',
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
                      },
                      '&.Mui-disabled': {
                        background: 'rgba(255,255,255,0.06)',
                        color: colors.neutral[400],
                      },
                    }}
                  >
                    {sending ? (
                      <CircularProgress size={16} sx={{ color: 'inherit' }} />
                    ) : (
                      <Send sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Box>
              </>
            ) : (
              /* Start conversation form */
              <Box
                sx={{
                  flex: 1,
                  px: 2.5,
                  py: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  overflowY: 'auto',
                }}
              >
                {/* Show thank you if just rated */}
                {ratingSubmitted && (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 1,
                      px: 2,
                      borderRadius: designTokens.radius.sm,
                      bgcolor: colors.success.subtle,
                      border: `1px solid ${colors.success.main}33`,
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: colors.success.main, fontWeight: 600 }}>
                      Thank you for your feedback!
                    </Typography>
                  </Box>
                )}

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: '1.1rem',
                  }}
                >
                  How can we help?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.neutral[500], fontSize: '0.8rem' }}
                >
                  Select a category and describe your issue. We usually respond within 15 minutes.
                </Typography>

                {/* Category chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  {categories.map((cat) => (
                    <Chip
                      key={cat}
                      label={cat}
                      size="small"
                      onClick={() => setCategory(cat)}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        bgcolor:
                          category === cat
                            ? colors.primary.subtle
                            : designTokens.glass.surface,
                        color:
                          category === cat
                            ? colors.primary.main
                            : colors.neutral[600],
                        border: `1px solid ${
                          category === cat
                            ? 'rgba(232, 135, 92, 0.3)'
                            : designTokens.glass.border
                        }`,
                        transition: `all ${designTokens.transition.fast}`,
                        '&:hover': {
                          bgcolor: colors.primary.subtle,
                          color: colors.primary.main,
                        },
                      }}
                    />
                  ))}
                </Box>

                {/* Message textarea */}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Describe your issue..."
                  value={initialMessage}
                  onChange={(e) => setInitialMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: designTokens.radius.sm,
                      bgcolor: designTokens.glass.surface,
                      fontSize: '0.85rem',
                      '& fieldset': { borderColor: designTokens.glass.border },
                      '&:hover fieldset': { borderColor: designTokens.glass.borderHover },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.primary.main,
                        borderWidth: 1,
                      },
                    },
                  }}
                />

                {/* Start button */}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleStartConversation}
                  disabled={!initialMessage.trim() || sending}
                  sx={{
                    background: colors.primary.gradient,
                    color: '#FFFFFF',
                    fontWeight: 700,
                    borderRadius: designTokens.radius.md,
                    py: 1.2,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
                      boxShadow: designTokens.shadow.glow,
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(255,255,255,0.06)',
                      color: colors.neutral[400],
                    },
                  }}
                >
                  {sending ? (
                    <CircularProgress size={20} sx={{ color: 'inherit' }} />
                  ) : (
                    'Start Conversation'
                  )}
                </Button>

                {/* C10: Previous conversation link */}
                {hasPreviousConversation && (
                  <Button
                    size="small"
                    startIcon={<History sx={{ fontSize: 16 }} />}
                    onClick={handleViewPrevious}
                    sx={{
                      color: colors.neutral[500],
                      fontSize: '0.78rem',
                      textTransform: 'none',
                      alignSelf: 'center',
                      '&:hover': {
                        color: colors.primary.main,
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    View previous conversation
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: `1px solid ${designTokens.glass.border}`,
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.neutral[400],
                fontSize: '0.6rem',
                letterSpacing: '0.03em',
              }}
            >
              Powered by CoffeeMates
            </Typography>
          </Box>
        </Box>
      </Slide>
    </>
  );
}
